import React, { Component, ErrorInfo, ReactNode } from 'react';
import logger from '../utils/logger';
import './ErrorBoundary.css';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(_error: Error): Partial<ErrorBoundaryState> {
    // エラー発生時に state を更新して fallback UI を表示
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // 現在のユーザー情報を取得（可能な場合）
    let userId: string | null = null;
    let storeId: number | null = null;

    try {
      // localStorage.getItem can throw in Safari private browsing mode
      const authSession = localStorage.getItem('authSession');
      if (authSession) {
        const session = JSON.parse(authSession);
        userId = session.userId;
        storeId = session.storeId;
      }
    } catch (e) {
      // localStorage アクセスエラーまたはセッション解析エラーは無視
      // Safari プライベートブラウジングモード等で発生する可能性がある
    }

    // エラー情報をロギング
    logger.error('Unhandled error caught by ErrorBoundary', {
      context: 'error_boundary',
      error: error.toString(),
      errorStack: error.stack,
      errorInfo: errorInfo.componentStack,
      userId,
      storeId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      screenResolution: {
        width: window.screen.width,
        height: window.screen.height
      }
    });

    // エラー発生をユーザーアクションとしても記録
    logger.userAction('unhandled_error_occurred', {
      errorType: error.name,
      errorMessage: error.message,
      userId,
      storeId,
      timestamp: new Date().toISOString()
    });

    // state に詳細情報を保存
    this.setState({
      error,
      errorInfo
    });
  }

  handleReset = (): void => {
    // リセットアクションをログ
    logger.userAction('error_boundary_reset', {
      errorType: this.state.error?.name,
      errorMessage: this.state.error?.message,
      timestamp: new Date().toISOString()
    });

    // エラー状態をリセット
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });

    // ページをリロード
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // フォールバック UI をレンダリング
      return (
        <div className="error-boundary-container">
          <div className="error-boundary-content">
            <h1 className="error-boundary-title">⚠️ エラーが発生しました</h1>
            <p className="error-boundary-message">
              申し訳ございません。予期しないエラーが発生しました。
            </p>
            <p className="error-boundary-submessage">
              問題が解決しない場合は、システム管理者にお問い合わせください。
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-boundary-details">
                <summary>エラー詳細（開発環境のみ）</summary>
                <pre className="error-boundary-stack">
                  {this.state.error.toString()}
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}

            <div className="error-boundary-actions">
              <button onClick={this.handleReset} className="error-boundary-button">
                アプリケーションを再読み込み
              </button>
            </div>
          </div>
        </div>
      );
    }

    // エラーがない場合は子コンポーネントをレンダリング
    return this.props.children;
  }
}

export default ErrorBoundary;
